import {
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Inject,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  TemplateRef,
  ViewChild,
  forwardRef
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { Subscription } from "rxjs";
import { takeUntil } from "rxjs/operators";

import { OverlayRef, OverlayService } from "@lantern/app/core/utils/overlays";
import { AnchorPositionStrategy } from "@lantern/app/core/utils/overlays/position-strategies";

import {
  BaseOptionsDirective,
  BaseSelectComponent,
  SELECT_CLASSES,
  SelectClasses,
  getSelectClasses
} from "../base";
import { AdHocOptionsDirective } from "../shared/ad-hoc-options.directive";
import {
  SelectDropdownComponent,
  SingleSelectWithDropdownComponent
} from "../shared/select-dropdown.component";
import { SingleSelectPlaceholderLayoutDirective } from "./placeholder-layout.directive";

@Component({
  selector: "ta-select",
  providers: [
    {
      provide: BaseSelectComponent,
      useExisting: forwardRef(() => SingleSelectComponent)
    },
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SingleSelectComponent),
      multi: true
    },
    {
      provide: SELECT_CLASSES,
      useValue: getSelectClasses("select")
    }
  ],
  templateUrl: "./single-select.template.html"
})
export class SingleSelectComponent<
  Model,
  Identifier extends number | string,
  FieldValue extends Model | Identifier
> extends BaseSelectComponent<Model, Identifier, FieldValue>
  implements
    SingleSelectWithDropdownComponent<Model, Identifier, FieldValue>,
    OnDestroy,
    OnInit,
    ControlValueAccessor {
  @ContentChild(SingleSelectPlaceholderLayoutDirective, { static: true })
  placeholderLayout: TemplateRef<void>;

  @ViewChild("dropdownAnchor", { static: true }) dropdownAnchor: ElementRef;

  @HostBinding("attr.tabindex")
  get tabIndex(): number {
    return this.disabled ? -1 : 0;
  }

  @Input()
  placeholder: string;

  @Input()
  valid: boolean = false;

  @Input()
  showValidation: boolean = false;

  @Input()
  get disabled(): boolean {
    return this._disabled;
  }
  set disabled(value: boolean) {
    value = value != null && value !== false;
    if (this._disabled === value) {
      return;
    }

    this._disabled = value;
    if (this._disabled) {
      this.open = false; // make sure dropdown is closed
      this.addHostClass(this.selectClasses.parentDisabled);
    } else {
      this.removeHostClass(this.selectClasses.parentDisabled);
    }
  }
  private _disabled: boolean;

  get open(): boolean {
    return this._open;
  }
  set open(value: boolean) {
    // always false when disabled or loading models
    if (this.disabled || this.loadingSelected) {
      value = false;
    } else {
      value = value != null && value !== false;
    }

    if (this.open === value) {
      return;
    }

    this._open = value;
    if (this.open) {
      this.getOptions();
      this.openDropdown();
      this.addHostClass(this.selectClasses.parentActive);
    } else {
      (this.elementRef.nativeElement as HTMLElement).focus();
      this.closeDropdown();
      this.removeHostClass(this.selectClasses.parentActive);
    }
  }
  private _open: boolean = false;

  @Input()
  get searchable(): boolean {
    return this._searchable;
  }
  set searchable(value: boolean) {
    this._searchable = value != null && value !== false;
  }
  private _searchable: boolean = false;

  @Input()
  get clearable(): boolean {
    return this._clearable;
  }
  set clearable(value: boolean) {
    this._clearable = value != null && value !== false;
  }
  private _clearable: boolean = false;

  @Input()
  get value(): FieldValue | undefined {
    return this._value;
  }
  set value(input: FieldValue | undefined) {
    if (this.value === input) {
      this.initializedModel = true;
      return;
    }

    this._value = input;
    this.updateSelectedModel(input);
  }
  private _value: FieldValue | undefined = undefined;

  @Output()
  valueChange: EventEmitter<FieldValue> = new EventEmitter<FieldValue>();

  @Input()
  get selectedModel(): Model | undefined {
    return this._selectedModel;
  }
  set selectedModel(model: Model | undefined) {
    let identifier: Identifier | undefined =
      model != null ? this.getIdentifierFromModel(model) : undefined;

    let value: FieldValue | undefined;
    if (model == null) {
      value = undefined;
    } else if (this.valueBy === "model") {
      value = model as FieldValue;
    } else {
      value = this.getIdentifierFromModel(model) as FieldValue;
    }

    if (this.selectedIdentifier !== identifier) {
      this.selectedIdentifier = identifier;

      this.value = value;
      this._selectedModel = model;

      this.valueChange.emit(value);
      this.selectedModelChange.emit(model);
    }

    if (!this.initializedModel) {
      this.initializedModel = true;
    }
  }
  private _selectedModel: Model | undefined;

  @Output()
  selectedModelChange: EventEmitter<Model> = new EventEmitter<Model>();

  private selectedIdentifier: Identifier | undefined;

  constructor(
    injector: Injector,
    optionsSourceDirective: BaseOptionsDirective<Model, Identifier>,
    @Inject(SELECT_CLASSES) selectClasses: SelectClasses,
    public elementRef: ElementRef,
    private overlayService: OverlayService,
    renderer: Renderer2
  ) {
    super(
      injector,
      optionsSourceDirective,
      selectClasses,
      elementRef,
      renderer
    );
    this.initControlAccessors();

    this._searchable = !(
      optionsSourceDirective instanceof AdHocOptionsDirective
    );
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.setClasses();

    this.loadedOptions
      .pipe(takeUntil(this.onDestroyEmitter))
      .subscribe(() => this.updateSelectedModel(this.value));
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.closeDropdown();
  }

  setSelected(model?: Model): void {
    this.selectedModel = model;
  }

  isSelected(model: Model): boolean {
    return this.getIdentifierFromModel(model) === this.selectedIdentifier;
  }

  clearValue(event?: MouseEvent): void {
    if (event != null) {
      event.stopPropagation();
    }
    this.selectedModel = undefined;
  }

  get loadingSelected(): boolean {
    return this._loadingSelected;
  }
  private _loadingSelected: boolean = false;

  private updateSelectedModelSubscription?: Subscription;
  private updateSelectedModel(value?: FieldValue): void {
    if (
      this.updateSelectedModelSubscription != null &&
      !this.updateSelectedModelSubscription.closed
    ) {
      this.updateSelectedModelSubscription.unsubscribe();
    }

    if (value == null) {
      this._loadingSelected = false;
      this.updateSelectedModelSubscription = undefined;
      this.selectedModel = undefined;
      return;
    }

    this._loadingSelected = true;
    this.updateSelectedModelSubscription = this.getModelsFromFieldValues([
      value
    ]).subscribe(
      ([model]) => {
        this._loadingSelected = false;
        this.selectedModel = model;
      },
      () => {
        // TODO: error, failure to lookup value
        this._loadingSelected = false;
        throw new Error();
      }
    );
  }

  private dropdownOverlayRef?: OverlayRef;
  private dropdownPositionStrategy?: AnchorPositionStrategy;
  private openDropdown(): void {
    if (this.dropdownOverlayRef != null) {
      return;
    }

    if (this.dropdownPositionStrategy == null) {
      this.dropdownPositionStrategy = new AnchorPositionStrategy(
        this.dropdownAnchor.nativeElement,
        ["bottom-left", "bottom-right", "top-left", "top-right"],
        4
      );
    }
    this.dropdownOverlayRef = this.overlayService.createComponent(
      SelectDropdownComponent,
      this.dropdownPositionStrategy,
      this.injector
    );
  }

  private closeDropdown(): void {
    if (this.dropdownOverlayRef == null) {
      return;
    }
    this.dropdownOverlayRef.destroy();
    this.dropdownOverlayRef = undefined;
  }

  private setClasses(): void {
    this.renderer.addClass(
      this.dropdownAnchor.nativeElement,
      this.selectClasses.selection
    );
  }

  @HostListener("click", ["$event"])
  onClickInteraction(event: Event): void {
    event.preventDefault();
    this.touched = true;
    this.open = !this.open;
  }

  @HostListener("keydown.space", ["$event"])
  onSpacePress(event: Event): void {
    event.preventDefault();
    this.touched = true;
    this.open = !this.open;
  }

  @HostListener("window:resize", ["$event"])
  onWindowResize(): void {
    this.open = false;
  }

  initControlAccessors(): void {
    super.initControlAccessors();

    this.valueChange
      .pipe(takeUntil(this.onDestroyEmitter))
      .subscribe((value) => {
        if (this._onChangeFn == null || !this.initializedModel) {
          return;
        }
        this._onChangeFn(value);
      });
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
